import axios from 'axios';
import { config } from '../config/env';
import { v4 as uuidv4 } from 'uuid';

const API_URL = config.API_URL;

const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: {
        'Content-Type': 'application/json'
    },
    withCredentials: true // Required for sending cookies with requests
});

// Add auth token, workspace ID, and correlation ID to requests
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // Add workspace ID to all requests except auth endpoints
        const workspaceId = localStorage.getItem('currentWorkspaceId');
        const excludedPaths = ['/auth/login', '/auth/register', '/auth/me', '/auth/logout'];
        const isExcluded = excludedPaths.some(path => config.url?.includes(path));

        if (workspaceId && !isExcluded) {
            // Add as query parameter
            config.params = {
                ...config.params,
                workspaceId
            };
        }

        // Add correlation ID for request tracking
        // Use existing correlation ID from config, or generate new one
        const correlationId = config.correlationId || uuidv4();
        config.headers['X-Correlation-ID'] = correlationId;

        // Store correlation ID in config metadata for error handling
        config.metadata = {
            ...config.metadata,
            correlationId,
            startTime: Date.now()
        };

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Handle auth errors and token refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

api.interceptors.response.use(
    (response) => {
        // Capture correlation ID from response headers
        const correlationId = response.headers['x-correlation-id'];
        if (correlationId) {
            response.correlationId = correlationId;
        }

        // Calculate request duration if startTime is available
        const startTime = response.config?.metadata?.startTime;
        if (startTime) {
            response.duration = Date.now() - startTime;
        }

        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        // Attach correlation ID to error for debugging
        if (originalRequest?.metadata?.correlationId) {
            error.correlationId = originalRequest.metadata.correlationId;
        }

        // Also try to get it from response headers if available
        if (error.response?.headers?.['x-correlation-id']) {
            error.correlationId = error.response.headers['x-correlation-id'];
        }

        // If we get a 401 and haven't tried to refresh yet
        if (error.response?.status === 401 && !originalRequest._retry) {
            // Don't try to refresh for auth endpoints (login, register, etc.) or refresh endpoints themselves
            const authEndpoints = [
                '/auth/login',
                '/auth/register',
                '/auth/forgot-password',
                '/auth/reset-password',
                '/auth/refresh',
                '/auth/oauth/refresh'
            ];
            const isAuthEndpoint = authEndpoints.some(endpoint => originalRequest.url?.includes(endpoint));

            if (isAuthEndpoint) {
                // For auth endpoints, just return the error (wrong password, etc.)
                return Promise.reject(error);
            }

            const refreshToken = localStorage.getItem('refreshToken');

            // Determine if this is an OAuth user (no JWT in localStorage)
            const isOAuthUser = !refreshToken;

            if (isRefreshing) {
                // If already refreshing, queue this request
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(token => {
                    // For JWT users, update Authorization header
                    if (!isOAuthUser && token) {
                        originalRequest.headers['Authorization'] = 'Bearer ' + token;
                    }
                    // For OAuth users, cookies are sent automatically
                    return api(originalRequest);
                }).catch(err => {
                    return Promise.reject(err);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                if (isOAuthUser) {
                    // OAuth user: refresh using cookies
                    console.log('Refreshing OAuth token...');
                    await api.post('/auth/oauth/refresh');
                    // No need to update headers - cookies are sent automatically
                    processQueue(null, null);
                } else {
                    // JWT user: refresh using refresh token
                    console.log('Refreshing JWT token...');
                    const response = await api.post('/auth/refresh', { refreshToken });
                    const { accessToken, refreshToken: newRefreshToken } = response.data;

                    localStorage.setItem('token', accessToken);
                    if (newRefreshToken) {
                        localStorage.setItem('refreshToken', newRefreshToken);
                    }

                    // Update the authorization header
                    api.defaults.headers.common['Authorization'] = 'Bearer ' + accessToken;
                    originalRequest.headers['Authorization'] = 'Bearer ' + accessToken;
                    processQueue(null, accessToken);
                }

                isRefreshing = false;

                // Retry the original request
                return api(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError, null);
                isRefreshing = false;

                // Token refresh failed, log out
                console.log('Token refresh failed, logging out...');

                if (!isOAuthUser) {
                    // Clear JWT tokens from localStorage
                    localStorage.removeItem('token');
                    localStorage.removeItem('refreshToken');
                    localStorage.removeItem('user');
                }

                // Redirect to login for both OAuth and JWT users
                const currentPath = window.location.pathname;
                const publicPaths = ['/login', '/signup', '/forgot-password', '/reset-password', '/verify-email', '/invite', '/admin/login'];
                const isPublicPath = publicPaths.some(path => currentPath.startsWith(path));

                if (!isPublicPath) {
                    window.location.href = '/login';
                }

                return Promise.reject(refreshError);
            }
        }

        // For non-401 errors, just return the error without logging out
        return Promise.reject(error);
    }
);

// Execution Plan API
export const executionPlanApi = {
    parseDocument: (file, context = '') => {
        const formData = new FormData();
        formData.append('document', file);
        if (context) formData.append('context', context);
        return api.post('/execution-plans/parse-document', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    parseChatMessage: (message) => api.post('/execution-plans/parse-message', { message }),
    createExecutionPlan: (data) => api.post('/execution-plans', data),
    getExecutionPlans: (params) => api.get('/execution-plans', { params }),
    getExecutionPlan: (id) => api.get(`/execution-plans/${id}`),
    getExecutionPlanHistory: (id, params) => api.get(`/execution-plans/${id}/executions`, { params }),
    updateExecutionPlan: (id, data) => api.put(`/execution-plans/${id}`, data),
    deleteExecutionPlan: (id) => api.delete(`/execution-plans/${id}`),
    duplicateExecutionPlan: (id) => api.post(`/execution-plans/${id}/duplicate`),
    executeExecutionPlan: (id, config) => api.post(`/execution-plans/${id}/execute`, config),
    getExecutionStatus: (executionId) => api.get(`/execution-plans/executions/${executionId}`),
    cancelExecution: (executionId) => api.post(`/execution-plans/executions/${executionId}/cancel`),
    addGoalToPlan: (planId, goalId, order) => api.post(`/execution-plans/${planId}/goals`, { goalId, order }),
    bulkAddGoalsToPlan: (planId, goalIds) => api.post(`/execution-plans/${planId}/goals/bulk`, { goalIds })
};

export const testApi = {
    bulkRerunTestGoals: (goalIds, payload = {}) => {
        const data = typeof payload === 'string'
            ? { goalIds, executionMode: payload }
            : { goalIds, ...payload };
        return api.post('/tests/goals/bulk-rerun', data);
    },
    bulkDeleteTestGoals: (goalIds) => api.post('/tests/goals/bulk-delete', { goalIds })
};

export const workspaceApi = {
    // Workspace settings
    updateWorkspace: (id, data) => api.put(`/workspaces/${id}`, data),
    deleteWorkspace: (id) => api.delete(`/workspaces/${id}`),

    // Members
    getMembers: (workspaceId) => api.get(`/workspaces/${workspaceId}/members`),
    updateMemberRole: (workspaceId, userId, role) => api.put(`/workspaces/${workspaceId}/members/${userId}`, { role }),
    removeMember: (workspaceId, userId) => api.delete(`/workspaces/${workspaceId}/members/${userId}`),

    // Invitations
    inviteMember: (workspaceId, email, role, message) => api.post(`/workspaces/${workspaceId}/invite`, { email, role, message }),
    getInvitations: (workspaceId) => api.get(`/workspaces/${workspaceId}/invitations`),
    revokeInvitation: (workspaceId, inviteId) => api.delete(`/workspaces/${workspaceId}/invitations/${inviteId}`),
    resendInvitation: (workspaceId, inviteId) => api.post(`/workspaces/${workspaceId}/invitations/${inviteId}/resend`),

    // Invitation acceptance (by invited user)
    getInviteDetails: (token) => api.get(`/workspaces/invite/${token}`),
    acceptInvite: (token) => api.post(`/workspaces/invite/${token}/accept`),
    declineInvite: (token) => api.post(`/workspaces/invite/${token}/decline`),

    // Ownership
    transferOwnership: (workspaceId, newOwnerId) => api.post(`/workspaces/${workspaceId}/transfer-ownership`, { newOwnerId }),

    // Active Devices
    getDevices: (workspaceId) => api.get(`/workspaces/${workspaceId}/devices`),
    killDevice: (workspaceId, deviceId) => api.post(`/workspaces/${workspaceId}/devices/${deviceId}/kill`)
};

export const apiTokenApi = {
    list: (workspaceId) => api.get('/api-tokens', { params: { workspaceId } }),
    create: (data) => api.post('/api-tokens', data),
    revoke: (id) => api.delete(`/api-tokens/${id}`)
};

export const testAssetApi = {
    list: (workspaceId) => api.get(`/test-assets/workspace/${workspaceId}`),
    getUploadUrl: (params) => api.get('/test-assets/upload-url', { params }),
    create: (workspaceId, data) => api.post(`/test-assets/workspace/${workspaceId}`, data),
    update: (id, data) => api.put(`/test-assets/${id}`, data),
    delete: (id) => api.delete(`/test-assets/${id}`)
};

export const adminApi = {
    getStats: (params) => api.get('/admin/stats', { params }),
    getUsers: (params) => api.get('/admin/users', { params }),
    getWorkspaces: (params) => api.get('/admin/workspaces', { params }),
    getApps: (params) => api.get('/admin/apps', { params }),
    getSubscriptions: (params) => api.get('/admin/subscriptions', { params }),
    getDevices: () => api.get('/admin/devices'),
    killDevice: (deviceId) => api.post(`/admin/devices/${deviceId}/kill`)
};

export const featureFlagApi = {
    getFeatureFlags: (workspaceId) => api.get('/feature-flags', { params: { workspaceId } }),
};

export const categoriesApi = {
    list: (workspaceId) => api.get('/categories', { params: { workspaceId } }),
    create: (data) => api.post('/categories', data),
    update: (id, data) => api.put(`/categories/${id}`, data),
    delete: (id) => api.delete(`/categories/${id}`)
};

export default api;

